import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelectedAccount } from '@ibk/shared-auth';
import { TranslationProvider, useTranslation } from '@ibk/shared-translation';
import { Breadcrumb, Dropdown, Icon, Loading } from '@ibk/shared-ui';
import { TaxReportStatus, useGenerateTaxReportMutation } from '../../services/taxReportService';
import { contaPjTranslations } from '../../translations';
import styles from './IncomeReport.module.css';
const MAX_POLLING_ATTEMPTS = 15;
const POLLING_INTERVAL_MS = [1500, 500, 300];
const getPollingInterval = (attempt) => {
    if (attempt > POLLING_INTERVAL_MS.length)
        return POLLING_INTERVAL_MS.at(-1);
    return POLLING_INTERVAL_MS[attempt - 1];
};
export function IncomeReportBase() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { selectedAccount } = useSelectedAccount();
    const [selectedYear, setSelectedYear] = useState();
    const [viewState, setViewState] = useState('form');
    const [reportData, setReportData] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [isPolling, setIsPolling] = useState(false);
    const pollingIntervalRef = useRef(null);
    const pollingAttemptsRef = useRef(0);
    const [generateTaxReport, { isLoading }] = useGenerateTaxReportMutation();
    const yearOptions = [{ value: 2025, label: String(2025) }];
    const handleBack = () => {
        navigate('/home');
    };
    const stopPolling = () => {
        if (pollingIntervalRef.current) {
            clearTimeout(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
        setIsPolling(false);
    };
    const pollTaxReport = async (accountId, year) => {
        try {
            pollingAttemptsRef.current += 1;
            if (pollingAttemptsRef.current > MAX_POLLING_ATTEMPTS) {
                stopPolling();
                setErrorMessage(t('income-report.error.message'));
                setViewState('error');
                return;
            }
            const result = await generateTaxReport({ accountId, year }).unwrap();
            if (result.status === TaxReportStatus.GENERATED) {
                stopPolling();
                setReportData(result.documentBytes?.data ?? null);
                setViewState('success');
            }
            else if (result.status === TaxReportStatus.ERROR) {
                stopPolling();
                setErrorMessage(result.message || t('income-report.error.message'));
                setViewState('error');
            }
            else {
                stopPolling();
                const nextInterval = getPollingInterval(pollingAttemptsRef.current + 1);
                pollingIntervalRef.current = setTimeout(() => pollTaxReport(accountId, year), nextInterval);
            }
        }
        catch (error) {
            stopPolling();
            const err = error;
            setErrorMessage(err?.data?.message ?? t('income-report.error.message'));
            setViewState('error');
        }
    };
    const handleGenerateReport = async () => {
        const accountId = selectedAccount?.accountId;
        if (!accountId || !selectedYear) {
            setErrorMessage(t('income-report.error.message'));
            setViewState('error');
            return;
        }
        stopPolling();
        setViewState('loading');
        pollingAttemptsRef.current = 0;
        try {
            const result = await generateTaxReport({
                accountId,
                year: selectedYear,
            }).unwrap();
            if (result.status === TaxReportStatus.GENERATED) {
                setReportData(result.documentBytes?.data ?? null);
                setViewState('success');
            }
            else if (result.status === TaxReportStatus.ERROR) {
                setErrorMessage(result.message || t('income-report.error.message'));
                setViewState('error');
            }
            else {
                setIsPolling(true);
                const firstInterval = getPollingInterval(1);
                pollingIntervalRef.current = setTimeout(() => pollTaxReport(accountId, selectedYear), firstInterval);
            }
        }
        catch (error) {
            const err = error;
            setErrorMessage(err?.data?.message ?? t('income-report.error.message'));
            setViewState('error');
        }
    };
    const handleDownload = () => {
        if (!reportData)
            return;
        try {
            const uint8Array = new Uint8Array(reportData);
            const blob = new Blob([uint8Array], { type: 'application/pdf' });
            const url = globalThis.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `informe-rendimentos-${selectedYear}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            globalThis.URL.revokeObjectURL(url);
        }
        catch (error) {
            console.error('Error downloading report:', error);
        }
    };
    const handleTryAgain = () => {
        setViewState('form');
        setErrorMessage('');
        setReportData(null);
        pollingAttemptsRef.current = 0;
    };
    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, []);
    const breadcrumbItems = [
        {
            label: t('income-report.breadcrumb.home'),
            onClick: handleBack,
        },
        {
            label: t('income-report.breadcrumb.income-report'),
        },
    ];
    const renderFormView = () => (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.header, children: _jsx("div", { className: styles.title, children: t('income-report.title') }) }), _jsxs("div", { className: styles.body, children: [_jsxs("p", { className: styles.subtitle, children: [_jsx("strong", { children: t('income-report.subtitle') }), t('income-report.subtitle.base')] }), _jsx("div", { className: styles.formSection, children: _jsx(Dropdown, { label: t('income-report.year-label'), options: yearOptions, value: selectedYear, onChange: value => setSelectedYear(value), dataTestId: 'income-report-year-select', size: 'large' }) }), _jsxs("div", { className: styles.actions, children: [_jsx("button", { type: 'button', className: styles.buttonOutline, "data-testid": 'income-report-back-button', onClick: handleBack, disabled: isLoading || isPolling, children: t('income-report.button.back') }), _jsx("button", { type: 'button', className: styles.buttonPrimary, "data-testid": 'income-report-generate-button', onClick: handleGenerateReport, disabled: !selectedYear || isLoading || isPolling, children: isLoading || isPolling ? t('income-report.button.generating') : t('income-report.button.generate') })] })] })] }));
    const renderSuccessView = () => (_jsxs("div", { className: styles.body, children: [_jsxs("div", { className: styles.errorContent, children: [_jsx(Icon, { name: 'circleCheckmark', alt: 'Success icon', className: styles.successIcon }), _jsx("h2", { className: styles.errorTitle, children: t('income-report.success.title') }), _jsx("p", { className: styles.errorMessage, children: t('income-report.success.message') })] }), _jsxs("div", { className: styles.actions, children: [_jsx("button", { type: 'button', className: styles.buttonOutline, "data-testid": 'income-report-back-button', onClick: handleBack, children: t('income-report.button.back-to-home') }), _jsx("button", { type: 'button', className: styles.buttonPrimary, "data-testid": 'income-report-download-button', onClick: handleDownload, children: t('income-report.button.download') })] })] }));
    const renderLoadingView = () => (_jsx("div", { className: styles.body, children: _jsxs("div", { className: styles.errorContent, children: [_jsx(Loading, { size: 'medium' }), _jsx("h2", { className: styles.errorTitle, children: t('income-report.loading.title') }), _jsx("p", { className: styles.errorMessage, children: t('income-report.loading.message') })] }) }));
    const renderErrorView = () => (_jsxs("div", { className: styles.body, children: [_jsxs("div", { className: styles.errorContent, children: [_jsx(Icon, { name: 'circleError', alt: 'Error icon', className: styles.errorIcon }), _jsx("h2", { className: styles.errorTitle, children: t('income-report.error.title') }), _jsx("p", { className: styles.errorMessage, children: errorMessage || t('income-report.error.message') })] }), _jsxs("div", { className: styles.actions, children: [_jsx("button", { type: 'button', className: styles.buttonOutline, "data-testid": 'income-report-support-button', onClick: () => navigate('/suporte'), children: t('income-report.error.button.support') }), _jsx("button", { type: 'button', className: styles.buttonPrimary, "data-testid": 'income-report-try-again-button', onClick: handleTryAgain, children: t('income-report.error.button.retry') })] })] }));
    return (_jsxs("main", { className: styles.main, "data-testid": 'income-report', children: [_jsx("div", { className: styles.breadcrumbContainer, children: _jsx(Breadcrumb, { items: breadcrumbItems }) }), _jsxs("section", { className: styles.section, children: [viewState === 'form' && renderFormView(), viewState === 'loading' && renderLoadingView(), viewState === 'success' && renderSuccessView(), viewState === 'error' && renderErrorView()] })] }));
}
export function IncomeReport() {
    return (_jsx(TranslationProvider, { translations: contaPjTranslations, defaultLanguage: 'pt-BR', children: _jsx(IncomeReportBase, {}) }));
}
export default IncomeReport;
